import React from 'react'
import { ComponenteBaseEnlace, ImagenEnlace, PrecioEnlace } from './ComponenteBase'

const DiseñoGrid = ({ 
  enlaces = [], 
  estilosMiniatura = false, 
  esPreview = false,
  configuracion = {}
}) => {
  // Configuración por defecto para el diseño Grid
  const config = {
    columnas: 'auto', // 2 | 3 | 4 | auto
    espaciado: 'normal', // compact | normal | amplio
    aspecto: 'cuadrado', // cuadrado | rectangular | auto
    animacion: 'scale', // none | scale | rotate | float
    ...configuracion
  }

  // Clases base según configuración
  const obtenerClasesGrid = () => {
    let clases = ['grid gap-4']
    
    // Columnas
    if (config.columnas === '2') {
      clases.push('grid-cols-2')
    } else if (config.columnas === '3') {
      clases.push('grid-cols-1 sm:grid-cols-2 lg:grid-cols-3')
    } else if (config.columnas === '4') {
      clases.push('grid-cols-2 sm:grid-cols-3 lg:grid-cols-4')
    } else {
      // Auto - responsive basado en cantidad
      if (enlaces.length <= 2) {
        clases.push('grid-cols-1 sm:grid-cols-2')
      } else if (enlaces.length <= 4) {
        clases.push('grid-cols-2 sm:grid-cols-2')
      } else {
        clases.push('grid-cols-2 sm:grid-cols-3')
      }
    }
    
    // Espaciado
    if (config.espaciado === 'compact') {
      clases[clases.length - 1] = clases[clases.length - 1].replace('gap-4', 'gap-2')
    } else if (config.espaciado === 'amplio') {
      clases[clases.length - 1] = clases[clases.length - 1].replace('gap-4', 'gap-6')
    }
    
    return clases.join(' ')
  }

  const obtenerClasesItem = () => {
    let clases = ['bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 group cursor-pointer']
    
    // Aspecto
    if (config.aspecto === 'cuadrado') {
      clases.push('aspect-square')
    } else if (config.aspecto === 'rectangular') {
      clases.push('aspect-[4/3]')
    }
    
    // Animación
    if (config.animacion === 'scale') {
      clases.push('hover:scale-105')
    } else if (config.animacion === 'rotate') {
      clases.push('hover:rotate-1 hover:scale-105')
    } else if (config.animacion === 'float') {
      clases.push('hover:-translate-y-2 hover:scale-105')
    }
    
    return clases.join(' ')
  }

  // Ajustar para estilos miniatura
  if (estilosMiniatura) {
    return (
      <div className="grid grid-cols-2 gap-1">
        {enlaces.map((enlace, index) => (
          <ComponenteBaseEnlace
            key={enlace.id || index}
            enlace={enlace}
            estilosMiniatura={estilosMiniatura}
            esPreview={esPreview}
            className="enlace-miniatura"
          >
            <div className="bg-white border border-gray-200 rounded-md p-2 hover:border-blue-500 transition-all duration-200 group aspect-square">
              <div className="flex flex-col h-full text-center space-y-1">
                {/* Título arriba */}
                <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-xs leading-tight truncate">
                  {enlace.titulo}
                </h3>
                
                {/* Imagen y precio */}
                <div className="flex-1 flex flex-col items-center justify-center gap-1">
                  <div className="flex-shrink-0">
                    <ImagenEnlace 
                      enlace={enlace} 
                      estilosMiniatura={estilosMiniatura}
                      mostrarIcono={false}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    {/* Precio para productos */}
                    {enlace.tipo === 'producto' && (
                      <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                    )}
                  </div>
                </div>
                
                {/* Descripción y precio para no productos */}
                <div className="space-y-1">
                  {enlace.descripcion && (
                    <p className="text-gray-600 text-xs leading-tight line-clamp-2">
                      {enlace.descripcion}
                    </p>
                  )}
                  {enlace.tipo !== 'producto' && (
                    <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                  )}
                </div>
              </div>
            </div>
          </ComponenteBaseEnlace>
        ))}
      </div>
    )
  }

  // Diseño normal para desktop
  return (
    <div className={obtenerClasesGrid()}>
      {enlaces.map((enlace, index) => (
        <ComponenteBaseEnlace
          key={enlace.id || index}
          enlace={enlace}
          estilosMiniatura={estilosMiniatura}
          esPreview={esPreview}
          className={obtenerClasesItem()}
        >
          <div className="p-4 h-full flex flex-col space-y-3">
            {/* Título arriba */}
            <div className="text-center">
              <h3 className="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-lg">
                {enlace.titulo}
              </h3>
            </div>
            
            {/* Imagen y precio */}
            <div className="flex-1 flex flex-col items-center justify-center gap-2">
              <div className="flex-shrink-0">
                <ImagenEnlace 
                  enlace={enlace} 
                  estilosMiniatura={estilosMiniatura}
                  mostrarIcono={false}
                />
              </div>
              <div className="flex flex-col gap-2">
                {/* Precio para productos */}
                {enlace.tipo === 'producto' && (
                  <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                )}
              </div>
            </div>
            
            {/* Descripción y elementos finales */}
            <div className="space-y-2">
              {enlace.descripcion && (
                <div className="text-center">
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {enlace.descripcion}
                  </p>
                </div>
              )}
              
              {/* Precio para enlaces que no son productos */}
              {enlace.tipo !== 'producto' && (
                <div className="text-center">
                  <PrecioEnlace enlace={enlace} estilosMiniatura={estilosMiniatura} />
                </div>
              )}
            </div>
          </div>
        </ComponenteBaseEnlace>
      ))}
    </div>
  )
}

export default DiseñoGrid 